import { isSetSupported } from "@/office/requirements";

/**
 * Verfügbarkeit einzelner Office.js-Schnittstellen für Excel.
 *
 * Excel ist von Office.js deutlich besser abgedeckt als PowerPoint: Blätter,
 * Bereiche, Tabellen, Diagramme und benannte Bereiche gibt es bereits ab
 * ExcelApi 1.1, und für nahezu jede Sammlung existiert eine
 * `*OrNullObject`-Variante, die statt eines Fehlers ein leeres Objekt liefert.
 * Deshalb trägt der Excel-Kurs deutlich mehr prüfbare Aufgaben als der
 * PowerPoint-Kurs.
 *
 * Das Manifest verlangt keinen Anforderungssatz (siehe Kommentar dort). Jede
 * Prüfung fragt die von ihr benötigten Schnittstellen zur Laufzeit ab und
 * weicht aus, statt Nutzer älterer Excel-Versionen auszusperren.
 *
 * Einen eigenen Desktop-Anforderungssatz gibt es zwar ("ExcelApiDesktop"), er
 * enthält aber nur Randthemen – Fenster und Bereiche, Bilder in Kopf- und
 * Fußzeilen, Rechtschreibprüfung, `Range.formulaArray`. Der Kurs braucht davon
 * nichts und fragt ihn deshalb nirgends ab.
 */

/** Anforderungssätze, die in den Kursen geprüft werden. */
export const excelApi = {
  /** Blätter, Bereiche mit Werten, Formeln, Werttypen und Zahlenformaten. */
  grundlagen: "1.1",
  /** Tabellen samt Kopfzeile, Ergebniszeile und Formatvorlage. */
  tabellen: "1.1",
  /** Diagramme einer Arbeitsmappe. */
  diagramme: "1.1",
  /** Benannte Bereiche der Arbeitsmappe. */
  namen: "1.1",
  /** Zusammengeführte Zellen, ausgeblendete Zeilen und Spalten, Blattschutz. */
  bereichsdetails: "1.2",
  /** Filterschaltflächen und verbundene Zeilen einer Tabelle, PivotTables. */
  tabellendetails: "1.3",
  /**
   * `getUsedRangeOrNullObject` – die Grundlage jeder Zellprüfung. Anders als
   * `getUsedRange()` (ab 1.2) wirft sie auf einem leeren Blatt keinen Fehler.
   */
  benutzterBereich: "1.4",
  /** Gültigkeitsbereich eines Namens: Arbeitsmappe oder einzelnes Blatt. */
  namensbereich: "1.4",
  /** Bedingte Formatierung samt aller Regeltypen. */
  bedingteFormatierung: "1.6",
  /** Zellformatvorlagen der Arbeitsmappe und die Vorlage eines Bereichs. */
  formatvorlagen: "1.7",
  /** Fixierte Bereiche, Registerfarbe, Hyperlinks, Formel eines Namens. */
  ansicht: "1.7",
  /** Schutz der gesamten Arbeitsmappe. */
  mappenschutz: "1.7",
  /** Diagrammart (Säule, Linie, Kreis, …). */
  diagrammart: "1.7",
  /** Datenüberprüfung: hinterlegte Regel und Gültigkeit der Zellinhalte. */
  datenueberpruefung: "1.8",
  /** Gitternetzlinien eines Blattes. */
  gitternetz: "1.8",
  /** Aufbau einer PivotTable: Zeilen-, Spalten- und Wertefelder, Berichtslayout. */
  pivotaufbau: "1.8",
  /** AutoFilter eines Blattes oder einer Tabelle. */
  autofilter: "1.9",
  /**
   * Formatangaben je Zelle über `Range.getCellProperties` – Zellformatvorlage,
   * Schrift, Füllung, Ausrichtung und Zeilenumbruch in einem einzigen Abruf.
   */
  zellformate: "1.9",
  /** Seiteneinrichtung, Druckbereich, Wiederholungszeilen. */
  seitenlayout: "1.9",
  /** Kommentare (Unterhaltungen) der Arbeitsmappe. */
  kommentare: "1.10",
  /**
   * Ob eine Kommentarunterhaltung als erledigt markiert ist. Ein
   * Anforderungssatz höher als die Kommentare selbst – deshalb ein eigener
   * Eintrag, damit ein Excel mit 1.10 die Kommentare trotzdem liefert.
   */
  kommentarStatus: "1.11",
  /** Datenschnitte zu Tabellen und PivotTables. */
  datenschnitte: "1.10",
  /** Kategorie eines Zahlenformats, z. B. „Währung“ oder „Datum“. */
  formatkategorien: "1.12",
  /**
   * Verbundene Zellen eines Bereichs über `Range.getMergedAreasOrNullObject`.
   * `Range.merge()` gibt es zwar schon ab 1.2, ist aber eine rein schreibende
   * Methode – einen lesbaren Zustand dazu gibt es erst ab 1.13.
   */
  verbundeneZellen: "1.13",
} as const;

export function supportsExcelApi(version: string): boolean {
  return isSetSupported("ExcelApi", version);
}
